import { useEffect, useState } from "react";
import { Save, Trash2, AlertTriangle, Loader2 } from "lucide-react";
import ChangePasswordCard from "../../components/ChangePasswordCard";
import api, { fmtErr } from "../../lib/api";
import { toast } from "sonner";

const inputCls = "w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-shipany-blue focus:outline-none focus:ring-2 focus:ring-shipany-blue/15";

export default function AdminSettings() {
  const [settings, setSettings] = useState(null);
  const [plan, setPlan] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then(({ data }) => setSettings(data)).catch(() => {});
  }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.put("/admin/settings", settings);
      toast.success("Settings saved");
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const previewCleanup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/maintenance/clear-test-data", { dry_run: true });
      setPlan(data);
      toast.success("Dry run complete — nothing deleted yet");
    } catch (err) { toast.error(fmtErr(err)); } finally { setBusy(false); }
  };

  const runCleanup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/maintenance/clear-test-data", { dry_run: false, confirm: confirmText });
      setPlan(data);
      setConfirmText("");
      toast.success("Test data deleted");
    } catch (err) { toast.error(fmtErr(err)); } finally { setBusy(false); }
  };

  if (!settings) return null;

  return (
    <div className="mx-auto max-w-xl" data-testid="admin-settings-page">
      <h1 className="font-display text-2xl font-bold tracking-tight text-shipany-navy">Platform Settings</h1>
      <p className="text-sm text-slate-500">Global SHIPANY configuration.</p>
      <form onSubmit={save} className="mt-6 space-y-4 rounded-2xl border border-slate-200 bg-white p-6">
        <div><label className="text-xs font-semibold text-slate-500">Brand name</label>
          <input data-testid="setting-brand" className={`${inputCls} mt-1`} value={settings.brand_name || ""} onChange={(e) => setSettings({ ...settings, brand_name: e.target.value })} /></div>
        <div><label className="text-xs font-semibold text-slate-500">Support email</label>
          <input data-testid="setting-support-email" className={`${inputCls} mt-1`} value={settings.support_email || ""} onChange={(e) => setSettings({ ...settings, support_email: e.target.value })} /></div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div><label className="text-xs font-semibold text-slate-500">Support phone</label>
            <input data-testid="setting-support-phone" className={`${inputCls} mt-1`} placeholder="+91 …" value={settings.support_phone || ""} onChange={(e) => setSettings({ ...settings, support_phone: e.target.value })} /></div>
          <div><label className="text-xs font-semibold text-slate-500">WhatsApp number</label>
            <input data-testid="setting-whatsapp" className={`${inputCls} mt-1`} placeholder="+91 …" value={settings.whatsapp_number || ""} onChange={(e) => setSettings({ ...settings, whatsapp_number: e.target.value })} /></div>
        </div>
        <div><label className="text-xs font-semibold text-slate-500">Office address</label>
          <textarea data-testid="setting-address" rows={2} className={`${inputCls} mt-1`} placeholder="Shown in the public footer" value={settings.address || ""} onChange={(e) => setSettings({ ...settings, address: e.target.value })} /></div>
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Social links (footer icons)</p>
          <div className="mt-2 grid gap-4 sm:grid-cols-2">
            {[["social_whatsapp", "WhatsApp channel"], ["social_instagram", "Instagram"], ["social_youtube", "YouTube"], ["social_linkedin", "LinkedIn"]].map(([k, label]) => (
              <div key={k}><label className="text-xs font-semibold text-slate-500">{label} URL</label>
                <input data-testid={`setting-${k.replace(/_/g, "-")}`} className={`${inputCls} mt-1`} placeholder="https://…" value={settings[k] || ""} onChange={(e) => setSettings({ ...settings, [k]: e.target.value })} /></div>
            ))}
          </div>
          <p className="mt-1.5 text-[11px] text-slate-400">Only configured networks show in the footer. WhatsApp falls back to the WhatsApp number above.</p>
        </div>
        <div><label className="text-xs font-semibold text-slate-500">Announcement banner</label>
          <input data-testid="setting-announcement" className={`${inputCls} mt-1`} placeholder="Optional site-wide message" value={settings.announcement || ""} onChange={(e) => setSettings({ ...settings, announcement: e.target.value })} /></div>
        <button data-testid="settings-save-btn" className="flex items-center gap-2 rounded-xl bg-shipany-blue px-6 py-2.5 text-sm font-bold text-white hover:bg-[#0648B3] glow-blue">
          <Save className="h-4 w-4" /> Save Settings
        </button>
      </form>

      <div className="mt-6"><ChangePasswordCard /></div>

      <div className="mt-6 rounded-2xl border border-red-200 bg-white p-6" data-testid="cleanup-danger-zone">
        <h2 className="font-display flex items-center gap-2 text-lg font-bold text-red-600"><AlertTriangle className="h-5 w-5" /> Clear test data</h2>
        <p className="mt-1.5 text-xs leading-relaxed text-slate-500">
          Deletes test bookings (domestic + international + forwarding legs), their orders/invoices/wallet entries, NDR &amp; returns cases,
          courier performance snapshots, routing/automation/cron/API/audit logs, notification history and generated artifacts.
          Keeps the <span className="font-bold text-shipany-navy">LAUNCHLAB warehouse and its bookings</span>, all user accounts, provider credentials,
          courier catalog &amp; rate cards, pricing/settings, API keys, wallet balances and all real-money records.
        </p>
        <button onClick={previewCleanup} disabled={busy} data-testid="cleanup-preview-btn"
          className="mt-4 flex items-center gap-2 rounded-xl border border-slate-300 px-5 py-2.5 text-sm font-bold text-slate-600 hover:bg-slate-50 disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Preview cleanup (dry run)
        </button>
        {plan && (
          <div className="mt-4 rounded-xl bg-slate-50 p-4 text-xs" data-testid="cleanup-result">
            <p className="font-bold text-shipany-navy">{plan.dry_run ? "Dry run — would delete:" : "Deleted:"}</p>
            <p className="mt-1 text-slate-500">Keeping warehouses: <span className="font-semibold">{plan.kept_warehouses.length ? plan.kept_warehouses.join(", ") : "none matched LAUNCHLAB"}</span> · kept shipments: {plan.kept_shipments}</p>
            <ul className="mono mt-2 grid grid-cols-2 gap-x-6 gap-y-1 text-slate-600">
              {Object.entries(plan.deleted).map(([k, v]) => <li key={k} data-testid={`cleanup-row-${k}`}>{k}: <span className="font-bold text-red-600">{v}</span></li>)}
            </ul>
            {!Object.keys(plan.deleted).length && <p className="mt-2 text-emerald-600 font-semibold">Nothing to delete — database is clean.</p>}
          </div>
        )}
        <div className="mt-4 flex flex-col gap-3 sm:flex-row">
          <input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} data-testid="cleanup-confirm-input"
            placeholder='Type DELETE TEST DATA to enable' className={`${inputCls} flex-1 mono`} />
          <button onClick={runCleanup} disabled={busy || confirmText !== "DELETE TEST DATA"} data-testid="cleanup-delete-btn"
            className="flex items-center justify-center gap-2 rounded-xl bg-red-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-red-700 disabled:opacity-40">
            <Trash2 className="h-4 w-4" /> Delete test data permanently
          </button>
        </div>
      </div>
    </div>
  );
}
